import React, { useState, useEffect, useCallback, useRef } from 'react';
import { taskAPI, projectAPI, settingsAPI, sprintAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getInitials } from '../lib/utils';
import { Plus, Loader2, GripVertical, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { MultiSelectFilter } from '../components/MultiSelectFilter';

const DEFAULT_KANBAN_STATUS = {
  backlog: { label: 'Backlog', color: '#6b7280', bg: '#f9fafb' },
  todo: { label: 'To Do', color: '#3b82f6', bg: '#eff6ff' },
  in_progress: { label: 'In Progress', color: '#f59e0b', bg: '#fffbeb' },
  ready_for_testing: { label: 'Ready for Testing', color: '#8b5cf6', bg: '#f5f3ff' },
  done: { label: 'Done', color: '#10b981', bg: '#ecfdf5' },
  released: { label: 'Released', color: '#22c55e', bg: '#f0fdf4' },
  cancelled: { label: 'Cancelled', color: '#ef4444', bg: '#fef2f2' },
};

const BG_MAP = { '#6b7280': '#f9fafb', '#3b82f6': '#eff6ff', '#f59e0b': '#fffbeb', '#8b5cf6': '#f5f3ff', '#10b981': '#ecfdf5', '#22c55e': '#f0fdf4', '#ef4444': '#fef2f2', '#0ea5e9': '#f0f9ff' };

const PRIORITY_COLORS = { high: '#ef4444', urgent: '#ef4444', medium: '#f59e0b', low: '#3b82f6', none: '#94a3b8' };

const Kanban = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedAssignee, setSelectedAssignee] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState('all');
  const [sprints, setSprints] = useState([]);
  const [masterLabels, setMasterLabels] = useState([]);
  const [STATUS_CONFIG, setStatusConfig] = useState(DEFAULT_KANBAN_STATUS);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const isManager = user?.role && ['admin', 'super_admin', 'pm'].includes(user.role);

  const hasInitializedAssigneeFilter = useRef(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Default assignee to current user only on initial load
  useEffect(() => {
    if (!hasInitializedAssigneeFilter.current && user?.id && users.length > 0) {
      const userInList = users.some(u => u.id === user.id);
      if (userInList) setSelectedAssignee([user.id]);
      hasInitializedAssigneeFilter.current = true;
    }
  }, [user, users]);

  useEffect(() => {
    fetchTasks();
  }, [selectedClient]);

  const fetchInitialData = async () => {
    try {
      const [projectsRes, labelsRes, statusesRes, sprintsRes] = await Promise.all([
        projectAPI.getProjects().catch(() => ({ data: [] })),
        settingsAPI.getLabels().catch(() => ({ data: [] })),
        settingsAPI.getStatuses().catch(() => ({ data: [] })),
        sprintAPI.getSprints().catch(() => ({ data: [] })),
      ]);
      setClients(projectsRes.data || []);
      setMasterLabels(labelsRes.data || []);
      setSprints(sprintsRes.data || []);
      const dbStatuses = statusesRes.data || [];
      if (dbStatuses.length > 0) {
        const config = {};
        dbStatuses.forEach(s => {
          const key = s.key || s.value || s.name?.toLowerCase().replace(/\s+/g, '_');
          config[key] = { label: s.name || s.label, color: s.color, bg: BG_MAP[s.color] || '#f8fafc' };
        });
        setStatusConfig(config);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedClient !== 'all') params.client_id = selectedClient;
      const response = await taskAPI.getTasks(params);
      const taskList = response.data || [];
      setTasks(taskList);
      // Extract unique assignees from loaded tasks for the filter dropdown
      const assigneeMap = {};
      taskList.forEach(t => (t.assignees || []).forEach(a => {
        if (a && a.id) assigneeMap[a.id] = { id: a.id, name: a.name || a.email || a.id };
      }));
      setUsers(Object.values(assigneeMap));
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter tasks by search, assignee, label, and sprint
  const filteredTasks = tasks.filter(t => {
    if (selectedAssignee.length > 0) {
      const assigneeIds = (t.assignees || []).map(a => a.id || a);
      if (!selectedAssignee.some(id => assigneeIds.includes(id))) return false;
    }
    if (selectedLabel.length > 0) {
      if (!(t.labels || []).some(l => selectedLabel.includes(l))) return false;
    }
    if (selectedSprint !== 'all') {
      if ((t.cycle || '') !== selectedSprint) return false;
    }
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (t.title || '').toLowerCase().includes(q) ||
      (t.task_number || '').toLowerCase().includes(q);
  });

  // Group tasks by status
  const columns = Object.entries(STATUS_CONFIG).map(([status, config]) => ({
    status,
    config,
    tasks: filteredTasks.filter(t => t.status === status)
  }));

  // Drag & Drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
    // Add drag ghost styling
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedTask(null);
    setDragOverCol(null);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(status);
  };

  const handleDragLeave = () => {
    setDragOverCol(null);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverCol(null);
    if (!draggedTask || draggedTask.status === newStatus) return;

    // RBAC: developers can only drag their own tasks
    if (!isManager) {
      const assigneeIds = (draggedTask.assignees || []).map(a => a.id || a);
      const creatorId = draggedTask.created_by?.id;
      if (!assigneeIds.includes(user?.id) && creatorId !== user?.id) {
        return;
      }
    }

    // Optimistic update
    setTasks(prev => prev.map(t => t.id === draggedTask.id ? { ...t, status: newStatus } : t));

    try {
      await taskAPI.updateTask(draggedTask.id, { status: newStatus });
    } catch (error) {
      console.error('Failed to update task status:', error);
      // Revert on failure
      setTasks(prev => prev.map(t => t.id === draggedTask.id ? { ...t, status: draggedTask.status } : t));
    }
    setDraggedTask(null);
  };

  const getProjectName = (task) => task?.client?.name || '-';

  if (loading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col" data-testid="kanban-page">
      {/* Header */}
      <div className="flex flex-col gap-2 mb-3 sm:mb-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-lg sm:text-xl font-heading font-bold text-slate-800 whitespace-nowrap">Kanban Board</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-shrink-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 h-8 w-32 sm:w-56 text-sm border border-slate-200 rounded-lg outline-none focus:ring-1 focus:ring-[#002FA7]"
              data-testid="kanban-search"
            />
          </div>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="h-8 px-2 text-xs sm:text-sm border border-slate-200 rounded-lg bg-white outline-none flex-shrink-0 max-w-[120px] sm:max-w-none"
            data-testid="kanban-project-filter"
          >
            <option value="all">All Projects</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <MultiSelectFilter
            options={users.map(u => ({ value: u.id, label: u.name }))}
            selected={selectedAssignee}
            onChange={setSelectedAssignee}
            placeholder="Assignees"
            testId="kanban-assignee-filter"
          />
          <MultiSelectFilter
            options={masterLabels.map(l => ({ value: l.name, label: l.name, dot: l.color }))}
            selected={selectedLabel}
            onChange={setSelectedLabel}
            placeholder="Labels"
            testId="kanban-label-filter"
          />
          <select
            value={selectedSprint}
            onChange={(e) => setSelectedSprint(e.target.value)}
            className="h-8 px-2 text-xs sm:text-sm border border-slate-200 rounded-lg bg-white outline-none flex-shrink-0 max-w-[120px] sm:max-w-none"
            data-testid="kanban-sprint-filter"
          >
            <option value="all">All Sprints</option>
            {sprints.map(s => (
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden snap-x snap-mandatory sm:snap-none">
        <div className="flex gap-3 h-full sm:min-w-max pb-2">
          {columns.map(({ status, config, tasks: colTasks }) => (
            <div
              key={status}
              className={`w-[calc(100vw-3rem)] sm:w-[280px] flex-shrink-0 flex flex-col rounded-xl transition-colors snap-start ${
                dragOverCol === status ? 'ring-2 ring-[#002FA7] ring-opacity-50' : ''
              }`}
              style={{ background: config.bg }}
              onDragOver={(e) => handleDragOver(e, status)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, status)}
              data-testid={`kanban-col-${status}`}
            >
              {/* Column Header */}
              <div className="px-3 py-2.5 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: config.color }} />
                  <span className="text-sm font-semibold text-slate-700">{config.label}</span>
                  <span className="text-xs text-slate-400 bg-white/80 px-1.5 py-0.5 rounded-full">{colTasks.length}</span>
                </div>
              </div>

              {/* Cards */}
              <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2 min-h-[100px]">
                {colTasks.map(task => {
                  const pColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.none;
                  const _tk = new Date(); const _todayK = `${_tk.getFullYear()}-${String(_tk.getMonth()+1).padStart(2,'0')}-${String(_tk.getDate()).padStart(2,'0')}`;
                  const isOverdue = task.due_date && task.due_date.slice(0,10) < _todayK && !['done','released','cancelled'].includes(task.status);
                  const canDrag = isManager || (task.assignees || []).some(a => (a.id || a) === user?.id) || task.created_by?.id === user?.id;
                  return (
                    <div
                      key={task.id}
                      draggable={canDrag}
                      onDragStart={(e) => canDrag ? handleDragStart(e, task) : e.preventDefault()}
                      onDragEnd={handleDragEnd}
                      className={`bg-white rounded-lg border border-slate-200 p-3 ${canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-default opacity-75'} hover:shadow-sm transition-shadow ${
                        isOverdue ? 'border-l-2 border-l-red-400' : ''
                      }`}
                      data-testid={`kanban-card-${task.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <GripVertical className="h-4 w-4 text-slate-300 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-400 font-mono">{task.task_number}</p>
                          <p className="text-sm text-slate-800 font-medium mt-0.5 leading-snug">{task.title}</p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded" style={{ color: pColor, background: `${pColor}15` }}>
                              {task.priority || 'none'}
                            </span>
                            <span className="text-[10px] text-slate-400">{getProjectName(task)}</span>
                          </div>
                          {task.assignees?.length > 0 && (
                            <div className="flex -space-x-1 mt-2">
                              {task.assignees.slice(0, 3).map((a, i) => (
                                <div key={i} className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-600 border border-white" title={a.name}>
                                  {getInitials(a.name)}
                                </div>
                              ))}
                              {task.assignees.length > 3 && (
                                <div className="w-5 h-5 rounded-full bg-slate-300 flex items-center justify-center text-[8px] font-bold text-slate-600 border border-white">
                                  +{task.assignees.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                          {isOverdue && (
                            <span className="text-[9px] font-bold text-red-500 mt-1 block">OVERDUE</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {colTasks.length === 0 && (
                  <div className="text-center py-8 text-xs text-slate-400">
                    {dragOverCol === status ? 'Drop here' : 'No tasks'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Kanban;
